import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "../../../../i18n/LanguageContext";
import { decodeAndSanitizeHtml } from "./textAnalysisUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faChevronDown,
  faChevronUp,
  faExclamationTriangle,
  faCheckCircle,
  faShieldAlt,
  faTrash,
  faPhone,
  faLink,
  faBolt,
  faDollarSign,
  faInfoCircle,
  faSearch,
  faShareAlt,
  faEdit,
  faPaperPlane,
  faTimes,
  faCommentDots,
} from "@fortawesome/free-solid-svg-icons";
import ScamDetectorAPI from "../../services/ScamDetectorAPI";
import ScamReportAPI from "../../services/ScamReportAPI";
import { useLoading } from "../../../../contexts/LoadingContext";
import InternationalPhoneInput from "../../utils/InternationalPhoneInputUtil";
import RefreshButton from "../../common/RefreshButton";
import { formatDateOnly, formatTimeOnly } from "../../utils/DateTimeUtil";
import SmsAnalysisAPI from "../../services/SmsAnalysisAPI";
import DisclosuresModal from "../common/DisclosuresModal";
import OreInsightCard from "../common/OreInsightCard";
import SuggestionsBulletList from "../common/SuggestionsBulletList";
import InlineShareForm from "../common/InlineShareForm";
import useShareAlert from "../common/useShareAlert";
import ReanalyzeWithContextPanel from "../common/ReanalyzeWithContextPanel";
import AnalysisFeedbackBar from "../common/AnalysisFeedbackBar";
import SmsCheckStart from "./components/SmsCheckStart";
import { useSubscription } from "../../context/SubscriptionContext";
import HelpGuideButton from "../../HelpGuideButton";
import "../../css/Common.css";
import "./css/SmsAnalysisSimpleMobile.css";

// ─────────────────────────────────────────────
// Helper: risk classification
// ─────────────────────────────────────────────
const getRiskClass = (analysis) => {
  if (!analysis?.analysis?.analysisResult) return "unknown-risk";
  const { isScam, confidence } = analysis.analysis.analysisResult;
  if (isScam) {
    if (confidence >= 0.8) return "very-high-risk";
    if (confidence >= 0.6) return "high-risk";
    return "medium-risk";
  }
  return "low-risk";
};

const getRiskIcon = (analysis) => {
  const cls = getRiskClass(analysis);
  return cls === "low-risk" ? faCheckCircle : faExclamationTriangle;
};

const getConfidenceDescription = (confidence, isScam, t) => {
  const pct = Math.round((confidence || 0) * 100);
  if (isScam) {
    if (confidence >= 0.8)
      return t("sms.confidence.veryLikelyScam").replace("{pct}", pct);
    if (confidence >= 0.5)
      return t("sms.confidence.likelyScam").replace("{pct}", pct);
    return t("sms.confidence.possiblyScam").replace("{pct}", pct);
  }
  if (confidence >= 0.8)
    return t("sms.confidence.veryLikelySafe").replace("{pct}", pct);
  if (confidence >= 0.5)
    return t("sms.confidence.likelySafe").replace("{pct}", pct);
  return t("sms.confidence.possiblySafe").replace("{pct}", pct);
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Main Component
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const SmsAnalysisSimpleMobile = ({
  selectedAnalysis,
  analyses,
  handleAnalysisSelected,
  handleReanalyzeWithContext,
  refreshAnalyses,
  userUuid,
  setError,
  activeTab,
  setActiveTab,
  onAnalysisDeleted,
  showMobileOriginalMessage,
  setShowMobileOriginalMessage,
  showOriginalMessageInline,
  setShowOriginalMessageInline,
  reportIdMap = {},
  onNavigateToManual,
  userPhone,
  awaitState,
  onStartAwaiting,
  onCancelAwaiting,
  hasLoaded = true,
}) => {
  // ── Local state ──
  const [viewMode, setViewMode] = useState("list"); // "list" | "detail" | "manual"

  // ── Manual entry state ──
  const { showLoading, hideLoading } = useLoading();
  const [manualPhoneNumber, setManualPhoneNumber] = useState("");
  const [manualTextMessage, setManualTextMessage] = useState("");
  const [manualUserContext, setManualUserContext] = useState("");
  const [manualResult, setManualResult] = useState(null);
  const [manualError, setManualError] = useState(null);
  const [manualActiveSlide, setManualActiveSlide] = useState("form"); // "form" | "results"
  const [manualPhoneInputKey, setManualPhoneInputKey] = useState(0);
  const [expandedSections, setExpandedSections] = useState({
    suggestions: true,
    riskFactors: false,
    reasoning: true,
  });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [localAnalyses, setLocalAnalyses] = useState([]);
  const share = useShareAlert();
  const { canShare } = useSubscription();
  const isIndividualPlan = !canShare();
  const { t } = useTranslation();
  const detailRef = useRef(null);
  const slideDetailRef = useRef(null);
  const reanalyzeRef = useRef(null);

  const scrollToReanalyze = useCallback(() => {
    if (reanalyzeRef.current) {
      reanalyzeRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, []);

  // ── Derive sorted list (latest 5) ──
  useEffect(() => {
    if (!analyses || analyses.length === 0) {
      setLocalAnalyses([]);
      return;
    }
    const sorted = [...analyses].sort((a, b) => {
      const dateA = a.lastModified || new Date(0);
      const dateB = b.lastModified || new Date(0);
      return new Date(dateB) - new Date(dateA);
    });
    setLocalAnalyses(sorted.slice(0, 5));
  }, [analyses]);

  // ── Sync viewMode with parent activeTab ──
  useEffect(() => {
    if (activeTab === "details" && selectedAnalysis) {
      setViewMode("detail");
    }
  }, [activeTab, selectedAnalysis]);

  // ── When entering detail or manual view, scroll to top ──
  useEffect(() => {
    if (viewMode === "detail" || viewMode === "manual") {
      // Scroll the .hero ancestor (the actual scroll container on mobile)
      const heroEl = document.querySelector(".hero");
      if (heroEl) {
        heroEl.scrollTop = 0;
      }
      window.scrollTo(0, 0);
      if (slideDetailRef.current) {
        slideDetailRef.current.scrollTop = 0;
      }
      if (detailRef.current) {
        detailRef.current.scrollTop = 0;
      }
    }
  }, [viewMode, selectedAnalysis]);

  // ── Handlers ──
  const handleCardTap = useCallback(
    (analysis) => {
      handleAnalysisSelected(analysis);
      setViewMode("detail");
      setActiveTab("details");
      setExpandedSections({
        suggestions: true,
        riskFactors: false,
        reasoning: true,
      });
      setShowOriginalMessageInline(false);
    },
    [handleAnalysisSelected, setActiveTab, setShowOriginalMessageInline],
  );

  const handleBack = useCallback(() => {
    setViewMode("list");
    setActiveTab("results");
  }, [setActiveTab]);

  const handleManualPhoneChange = (value) => {
    setManualPhoneNumber(value);
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    if (!manualTextMessage.trim()) return;
    showLoading(
      t("textAnalysis.loading.title"),
      t("textAnalysis.loading.subtitle"),
    );
    setManualError(null);
    setManualResult(null);
    try {
      const result = await ScamDetectorAPI.analyzeText(
        manualPhoneNumber.trim() || null,
        manualTextMessage.trim(),
        manualUserContext.trim() || null,
      );
      setManualResult(result);
      setManualActiveSlide("results");
      const riskLevel =
        result?.aiAnalysis?.riskLevel ||
        result?.phoneAnalysis?.riskLevel ||
        "low";
      const isHighOrMed = ["high", "critical", "medium"].includes(
        (riskLevel || "").toLowerCase(),
      );
      if (isHighOrMed) {
        try {
          await ScamReportAPI.saveReport({
            analysisType: "TEXT_ANALYSIS",
            analysisTitle: "Text Message Scam Detected",
            analysisDetail: `Phone: ${manualPhoneNumber}`,
            analysisInput: manualTextMessage,
            context: manualUserContext?.trim() || undefined,
            isScam: result?.aiAnalysis?.isScam ?? true,
            confidence: result?.aiAnalysis?.confidence,
            riskLevel,
            explanation: result?.aiAnalysis?.explanation,
            suggestions: result?.aiAnalysis?.suggestions,
          });
        } catch (saveErr) {
          console.error(
            "[SmsAnalysisSimpleMobile] Failed to save report:",
            saveErr,
          );
        }
      }
    } catch (err) {
      console.error("[SmsAnalysisSimpleMobile] Manual analysis error:", err);
      setManualError(err.message || t("textAnalysis.error.generic"));
    } finally {
      hideLoading();
    }
  };

  const handleManualReset = () => {
    setManualPhoneNumber("");
    setManualTextMessage("");
    setManualUserContext("");
    setManualResult(null);
    setManualError(null);
    setManualActiveSlide("form");
    setManualPhoneInputKey((k) => k + 1);
  };

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await refreshAnalyses();
    } finally {
      setIsRefreshing(false);
    }
  }, [refreshAnalyses]);

  const handleDelete = useCallback(
    async (e, analysisItem) => {
      e.stopPropagation();
      try {
        await SmsAnalysisAPI.deleteSmsAnalysis(analysisItem.id);
        if (selectedAnalysis && selectedAnalysis.id === analysisItem.id) {
          handleAnalysisSelected(null);
          setViewMode("list");
          setActiveTab("results");
        }
        onAnalysisDeleted(analysisItem.id);
      } catch {
        setError("Failed to delete SMS analysis. Please try again.");
      }
    },
    [
      selectedAnalysis,
      handleAnalysisSelected,
      setActiveTab,
      setError,
      onAnalysisDeleted,
    ],
  );

  const toggleSection = useCallback((section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  }, []);

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // RENDER: Results List View
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const renderListView = () => (
    <div className="sasm-list-view">
      {/* Header */}
      <div className="sasm-header">
        <div className="sasm-header-top">
          <h1 className="main-title">{t("sms.page.title")}</h1>
          {localAnalyses.length > 0 && <HelpGuideButton guideId={2} />}
        </div>
        <p className="common-subtitle">{t("sms.page.subtitle")}</p>
      </div>

      {hasLoaded && (
        <SmsCheckStart
          variant={localAnalyses.length > 0 ? "compact" : "full"}
          userPhone={userPhone}
          awaitState={awaitState}
          onStartAwaiting={onStartAwaiting}
          onCancelAwaiting={onCancelAwaiting}
          onPasteInstead={() => setViewMode("manual")}
        />
      )}

      {localAnalyses.length > 0 && (
        <>
          {/* Results header */}
          <div className="sasm-results-header">
            <h2 className="sasm-results-title">
              {t("sms.results.heading")}
              <span className="sasm-results-count">{localAnalyses.length}</span>
            </h2>
            <RefreshButton
              onClick={handleRefresh}
              loading={isRefreshing}
              size="sm"
            />
          </div>

          {/* Cards list */}
          <div className="sasm-card-list">
            {localAnalyses.map((item) => {
              const result = item.analysis?.analysisResult;
              const isScam = result?.isScam;
              const riskClass = getRiskClass(item);
              const isSelected = selectedAnalysis?.id === item.id;
              const msgPreview = item.messageContent
                ? item.messageContent.substring(0, 120) +
                  (item.messageContent.length > 120 ? "..." : "")
                : "No message content";
              const date = formatDateOnly(item.lastModified, "short");
              const time = formatTimeOnly(item.lastModified, true);
              const confidence = getConfidenceDescription(
                result?.confidence,
                result?.isScam,
                t,
              );

              return (
                <button
                  key={item.id}
                  className={`sasm-card ${riskClass} ${isSelected ? "selected" : ""}`}
                  onClick={() => handleCardTap(item)}
                  type="button"
                >
                  <div className="sasm-card-risk-strip" />
                  <div className="sasm-card-body">
                    <div className="sasm-card-top">
                      <span className={`sasm-card-badge ${riskClass}`}>
                        <FontAwesomeIcon icon={getRiskIcon(item)} />
                        {isScam ? t("sms.badge.scam") : t("sms.badge.safe")}
                      </span>
                      <button
                        className="sasm-card-delete"
                        onClick={(e) => handleDelete(e, item)}
                        title={t("sms.card.deleteTitle")}
                        type="button"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                    <div className="sasm-card-message">
                      <span className="sasm-card-label">
                        {t("sms.card.message")}
                      </span>
                      <span className="sasm-card-value">{msgPreview}</span>
                    </div>
                    <div className="sasm-card-footer">
                      <span className="sasm-card-confidence">{confidence}</span>
                      <span className="sasm-card-date">
                        {date} {time}
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* Disclaimer */}
      <DisclosuresModal disclaimerText={t("sms.disclaimer")} />
    </div>
  );

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // RENDER: Detail View
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const renderDetailView = () => {
    if (!selectedAnalysis) {
      handleBack();
      return null;
    }

    const result = selectedAnalysis.analysis?.analysisResult;
    const isScam = result?.isScam;
    const confidence = result?.confidence || 0;
    const suggestions = result?.suggestions || "";
    const reasoning = result?.reasoning || "";
    const hasSuspiciousLinks = result?.hasSuspiciousLinks;
    const hasUrgentLanguage = result?.hasUrgentLanguage;
    const hasFinancialThreats = result?.hasFinancialThreats;

    // Build flags array
    const flags = [
      hasSuspiciousLinks && {
        icon: faLink,
        label: t("sms.flag.suspiciousLinks"),
      },
      hasUrgentLanguage && {
        icon: faBolt,
        label: t("sms.flag.urgentLanguage"),
      },
      hasFinancialThreats && {
        icon: faDollarSign,
        label: t("sms.flag.financialThreats"),
      },
    ].filter(Boolean);

    return (
      <div className="sasm-detail-view" ref={detailRef}>
        {/* Back button */}
        <button className="sasm-back-btn" onClick={handleBack} type="button">
          <FontAwesomeIcon icon={faArrowLeft} />
          <span>{t("sms.detail.back")}</span>
        </button>

        {/* Threat Assessment Banner (desktop style) */}
        <div className="case-details">
          <div
            className={`threat-banner sasm-threat-banner ${isScam ? "threat-detected" : confidence <= 0.2 ? "security-failed" : "safe"}`}
          >
            <div className="threat-icon">
              <FontAwesomeIcon
                icon={isScam ? faExclamationTriangle : faCheckCircle}
              />
            </div>
            <h2 className="threat-title">
              {isScam
                ? t("sms.detail.scamDetected")
                : t("sms.detail.noThreats")}
            </h2>
            {isScam && (
              <button
                className="threat-banner-share-btn"
                onClick={
                  isIndividualPlan
                    ? undefined
                    : (e) => {
                        e.stopPropagation();
                        share.toggleShare();
                      }
                }
                disabled={isIndividualPlan}
                title={
                  isIndividualPlan
                    ? t("sms.detail.shareIndividual")
                    : t("sms.detail.shareFamily")
                }
              >
                <FontAwesomeIcon
                  icon={faShareAlt}
                  className="threat-share-icon"
                />
                <span className="threat-share-label">
                  {t("sms.detail.share")}
                </span>
              </button>
            )}
          </div>
        </div>

        {/* Inline Share Form (no modal) */}
        {share.shareOpen && isScam && (
          <InlineShareForm
            shareSuccess={share.shareSuccess}
            shareError={share.shareError}
            shareMessage={share.shareMessage}
            setShareMessage={share.setShareMessage}
            shareLoading={share.shareLoading}
            onCancel={share.closeShare}
            onSend={() =>
              share.sendShare(
                selectedAnalysis?.id
                  ? reportIdMap[selectedAnalysis.id]
                  : undefined,
              )
            }
          />
        )}

        {/* Threat flags */}
        {flags.length > 0 && (
          <div className="sasm-flags">
            {flags.map((flag, i) => (
              <span key={i} className="sasm-flag-chip">
                <FontAwesomeIcon icon={flag.icon} />
                {flag.label}
              </span>
            ))}
          </div>
        )}

        {/* Suggestions section (desktop style) */}
        {suggestions && (
          <div className="suggestions-section">
            <h4 className="subsection-title">
              <FontAwesomeIcon
                icon={faInfoCircle}
                className="fa-icon-margin-right-8"
              />
              {t("sms.section.whatToDo")}
            </h4>
            <SuggestionsBulletList text={suggestions} />
          </div>
        )}

        {/* Explanation section (desktop style) */}
        {reasoning && (
          <div className="explanation-section">
            <h4
              className="subsection-title collapsible-header"
              onClick={() => toggleSection("reasoning")}
              style={{ cursor: "pointer" }}
            >
              <FontAwesomeIcon
                icon={faSearch}
                className="fa-icon-margin-right-8"
              />
              {t("sms.section.explanation")}
              <FontAwesomeIcon
                icon={expandedSections.reasoning ? faChevronUp : faChevronDown}
                style={{ marginLeft: "auto", fontSize: "0.85rem" }}
              />
            </h4>
            {expandedSections.reasoning && (
              <div
                className="explanation-text"
                dangerouslySetInnerHTML={{
                  __html: decodeAndSanitizeHtml(reasoning || ""),
                }}
              />
            )}
          </div>
        )}

        {/* Feedback bar */}
        <AnalysisFeedbackBar
          analysisKey={selectedAnalysis?.id}
          onNegativeFeedback={scrollToReanalyze}
        />

        {/* Re-analyze with Context */}
        <div ref={reanalyzeRef}>
          <ReanalyzeWithContextPanel
            initialContext={selectedAnalysis?.userContext || ""}
            onSubmit={(context) =>
              handleReanalyzeWithContext?.(selectedAnalysis, context)
            }
          />
        </div>

        <div className="sasm-sections">
          {/* Organization Verification */}
          <OreInsightCard
            oreData={selectedAnalysis?.analysis?.analysisResult}
            channel="sms"
            isMobile={true}
            accordionPrefix="sasm"
          />
        </div>
      </div>
    );
  };

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // MANUAL ENTRY VIEW
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const renderManualView = () => {
    const hasInput = manualPhoneNumber || manualTextMessage.trim();
    const aiResult = manualResult?.aiAnalysis;

    if (manualActiveSlide === "results" && manualResult) {
      return (
        <div className="sasm-manual-view">
          <button
            type="button"
            className="sasm-back-btn"
            onClick={() => setManualActiveSlide("form")}
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            {t("manual.results.backToForm")}
          </button>
          <div className="sasm-manual-result">
            <div
              className={`threat-banner ${aiResult?.isScam ? "threat-detected" : "safe"}`}
            >
              <div className="threat-icon">
                <FontAwesomeIcon
                  icon={
                    aiResult?.isScam ? faExclamationTriangle : faCheckCircle
                  }
                />
              </div>
              <h2 className="threat-title">
                {aiResult?.isScam
                  ? t("sms.detail.scamDetected")
                  : t("manual.results.appearsLegitimate")}
              </h2>
            </div>
            {aiResult?.explanation && (
              <div className="sasm-manual-result-section">
                <strong>{t("email.detail.explanation")}</strong>
                <p>{aiResult.explanation}</p>
              </div>
            )}
            {aiResult?.suggestions && (
              <div className="sasm-manual-result-section">
                <strong>{t("email.detail.suggestions")}</strong>
                <SuggestionsBulletList suggestions={aiResult.suggestions} />
              </div>
            )}
            <button
              type="button"
              className="btn btn-ghost sasm-manual-analyze-again-btn"
              onClick={handleManualReset}
            >
              {t("textAnalysis.form.analyzeBtn")}
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="sasm-manual-view">
        <button
          type="button"
          className="sasm-back-btn"
          onClick={() => {
            handleManualReset();
            setViewMode("list");
          }}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          {t("sms.mobile.backToList")}
        </button>

        <h1 className="sasm-title">
          <FontAwesomeIcon icon={faEdit} className="sasm-title-icon" />
          {t("textAnalysis.page.title")}
        </h1>
        <p className="sasm-subtitle">{t("textAnalysis.page.subtitle")}</p>

        {manualError && (
          <div className="sasm-error-banner">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <span>{manualError}</span>
          </div>
        )}

        <form onSubmit={handleManualSubmit} className="sasm-manual-form">
          <div className="sasm-manual-field">
            <div className="sasm-manual-field-label">
              <FontAwesomeIcon
                icon={faPhone}
                className="sasm-manual-field-icon"
              />
              <label>{t("textAnalysis.form.phoneLabel")}</label>
            </div>
            <InternationalPhoneInput
              key={manualPhoneInputKey}
              id="sasm-man-phone"
              value={manualPhoneNumber}
              onChange={handleManualPhoneChange}
              placeholder={t("textAnalysis.form.phonePlaceholder")}
              className="phone-input compact"
            />
          </div>
          <div className="sasm-manual-field">
            <div className="sasm-manual-field-label">
              <FontAwesomeIcon
                icon={faCommentDots}
                className="sasm-manual-field-icon"
              />
              <label>{t("textAnalysis.form.messageLabel")}</label>
            </div>
            <textarea
              value={manualTextMessage}
              onChange={(e) => setManualTextMessage(e.target.value)}
              placeholder={t("textAnalysis.form.messagePlaceholder")}
              rows={5}
              maxLength={1000}
              required
            />
          </div>
          <div className="sasm-manual-field">
            <div className="sasm-manual-field-label">
              <FontAwesomeIcon
                icon={faInfoCircle}
                className="sasm-manual-field-icon"
              />
              <label>{t("textAnalysis.form.contextLabel")}</label>
            </div>
            <textarea
              value={manualUserContext}
              onChange={(e) => setManualUserContext(e.target.value)}
              placeholder={t("textAnalysis.form.contextPlaceholder")}
              rows={3}
            />
          </div>
          <div className="btn-group">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!manualTextMessage.trim()}
            >
              <FontAwesomeIcon icon={faPaperPlane} />
              {t("textAnalysis.form.analyzeBtn")}
            </button>
            {hasInput && (
              <button
                type="button"
                className="btn btn-ghost"
                onClick={handleManualReset}
              >
                <FontAwesomeIcon icon={faTimes} />
                {t("textAnalysis.form.clearBtn")}
              </button>
            )}
          </div>
        </form>

        {manualResult && (
          <button
            type="button"
            className="btn btn-secondary sasm-manual-view-results-link"
            onClick={() => setManualActiveSlide("results")}
          >
            {t("manual.results.viewResults")}
          </button>
        )}
      </div>
    );
  };

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // MAIN RENDER
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  return (
    <div className="sasm-container">
      <div
        className={`sasm-view-slider ${viewMode === "detail" ? "sasm-show-detail" : viewMode === "manual" ? "sasm-show-manual" : ""}`}
      >
        <div className="sasm-slide sasm-slide-list">{renderListView()}</div>
        <div className="sasm-slide sasm-slide-detail" ref={slideDetailRef}>
          {viewMode === "detail" && renderDetailView()}
        </div>
        <div className="sasm-slide sasm-slide-manual">
          {viewMode === "manual" && renderManualView()}
        </div>
      </div>
    </div>
  );
};

export default SmsAnalysisSimpleMobile;
